import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getApiBaseUrl } from '../config/api';
import { resolveAvatarUrl, handleAvatarError } from '../utils/profilePicUrl';
import { getAuthorDisplayName } from '../utils/displayName';
import { REACTIONS } from '../utils/reactions';
import ReactionIcon from './icon/ReactionIcon';
import './MessagingDock.css';

const API_URL = getApiBaseUrl();
const POLL_MS = 4000;
const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp']);
const ACCEPT_FILES =
  'image/jpeg,image/png,image/gif,image/webp,image/bmp,.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.zip,.csv';

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return time;
  const date = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return `${date}, ${time}`;
}

function isImageAttachment(type) {
  return IMAGE_EXTS.has(String(type || '').toLowerCase());
}

function emptyReactions() {
  return { counts: {}, my_reaction: null, total: 0 };
}

function MessageReactionBar({ message, onReact }) {
  const [open, setOpen] = useState(false);
  const hideTimer = useRef(null);
  const reactions = message.reactions || emptyReactions();
  const counts = reactions.counts || {};
  const topTypes = Object.entries(counts)
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([type]) => type);

  const clearHide = () => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const show = () => {
    clearHide();
    setOpen(true);
  };

  const scheduleHide = () => {
    clearHide();
    hideTimer.current = setTimeout(() => setOpen(false), 220);
  };

  useEffect(() => () => clearHide(), []);

  return (
    <div className="msg-react-wrap">
      {topTypes.length > 0 && (
        <button
          type="button"
          className={`msg-react-chips ${reactions.my_reaction ? 'msg-react-chips--mine' : ''}`}
          onClick={() =>
            onReact(
              message.id,
              reactions.my_reaction || topTypes[0],
              Boolean(reactions.my_reaction)
            )
          }
          title="Reactions"
        >
          {topTypes.slice(0, 3).map((type) => (
            <ReactionIcon key={type} type={type} size={12} />
          ))}
          {reactions.total > 0 && <span>{reactions.total}</span>}
        </button>
      )}
      <div
        className="msg-react-trigger"
        onMouseEnter={show}
        onMouseLeave={scheduleHide}
      >
        <button
          type="button"
          className={`msg-react-btn ${reactions.my_reaction ? 'msg-react-btn--active' : ''}`}
          aria-label="React to message"
          title="React"
          onClick={() => {
            if (reactions.my_reaction) {
              onReact(message.id, reactions.my_reaction, true);
            } else {
              setOpen((v) => !v);
            }
          }}
        >
          {reactions.my_reaction ? (
            <ReactionIcon type={reactions.my_reaction} size={14} />
          ) : (
            <i className="far fa-smile" />
          )}
        </button>
        {open && (
          <div
            className="msg-react-picker"
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
            role="listbox"
            aria-label="Choose reaction"
          >
            {REACTIONS.map((r) => (
              <button
                key={r.type}
                type="button"
                className={`msg-react-pick ${
                  reactions.my_reaction === r.type ? 'msg-react-pick--active' : ''
                }`}
                title={r.label}
                aria-label={r.label}
                onClick={() => {
                  onReact(message.id, r.type, reactions.my_reaction === r.type);
                  setOpen(false);
                }}
              >
                <ReactionIcon type={r.type} size={18} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * LinkedIn-style messaging dock (bottom-right).
 * Chat is only allowed with accepted connections.
 */
export default function MessagingDock({ user }) {
  const userId = user?.id ? Number(user.id) : 0;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list'); // list | chat | compose
  const [conversations, setConversations] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [active, setActive] = useState(null); // { id, peer }
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [attachment, setAttachment] = useState(null); // File
  const [attachmentPreview, setAttachmentPreview] = useState(null); // object URL
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactQuery, setContactQuery] = useState('');
  const [error, setError] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const messagesEndRef = useRef(null);
  const lastMsgIdRef = useRef(0);
  const fileInputRef = useRef(null);

  const clearAttachment = useCallback(() => {
    setAttachment(null);
    setAttachmentPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const postJson = async (endpoint, body) => {
    const res = await fetch(`${API_URL}/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  };

  const fetchConversations = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/get_conversations.php?user_id=${userId}`);
      const data = await res.json();
      if (data.success) {
        setConversations(data.conversations || []);
        setTotalUnread(data.total_unread || 0);
      }
    } catch (err) {
      console.error('Failed to load conversations', err);
    }
  }, [userId]);

  const applyReactionMap = useCallback((reactionMap) => {
    if (!reactionMap || typeof reactionMap !== 'object') return;
    setMessages((prev) =>
      prev.map((m) => {
        const next = reactionMap[String(m.id)] || reactionMap[m.id];
        if (!next) return m;
        return { ...m, reactions: next };
      })
    );
  }, []);

  const applyMessageUpdates = useCallback((updates) => {
    if (!updates || typeof updates !== 'object') return;
    setMessages((prev) =>
      prev.map((m) => {
        const next = updates[String(m.id)] || updates[m.id];
        if (!next) return m;
        return { ...m, ...next };
      })
    );
  }, []);

  const fetchMessages = useCallback(
    async (conversationId, { incremental = false } = {}) => {
      if (!userId || !conversationId) return;
      try {
        const params = new URLSearchParams({
          user_id: String(userId),
          conversation_id: String(conversationId),
          limit: '80',
        });
        if (incremental && lastMsgIdRef.current > 0) {
          params.set('after_id', String(lastMsgIdRef.current));
        }
        const res = await fetch(`${API_URL}/get_messages.php?${params}`);
        const data = await res.json();
        if (!data.success) return;

        if (incremental) {
          const incoming = data.messages || [];
          if (incoming.length) {
            setMessages((prev) => {
              const seen = new Set(prev.map((m) => m.id));
              const merged = [...prev];
              incoming.forEach((m) => {
                if (!seen.has(m.id)) merged.push(m);
              });
              return merged;
            });
            lastMsgIdRef.current = Math.max(
              lastMsgIdRef.current,
              ...incoming.map((m) => m.id)
            );
          }
          applyReactionMap(data.reaction_map);
          applyMessageUpdates(data.message_updates);
        } else {
          const list = data.messages || [];
          setMessages(list);
          lastMsgIdRef.current = list.length
            ? Math.max(...list.map((m) => m.id))
            : 0;
          if (data.peer) {
            setActive((prev) =>
              prev ? { ...prev, peer: data.peer } : { id: conversationId, peer: data.peer }
            );
          }
        }

        await postJson('mark_conversation_read.php', {
          user_id: userId,
          conversation_id: conversationId,
        });
        fetchConversations();
      } catch (err) {
        console.error('Failed to load messages', err);
      }
    },
    [userId, fetchConversations, applyReactionMap, applyMessageUpdates]
  );

  const openConversation = async (conv) => {
    setError('');
    setView('chat');
    setActive({ id: conv.id, peer: conv.peer });
    setMessages([]);
    lastMsgIdRef.current = 0;
    setDraft('');
    setEditingId(null);
    setEditDraft('');
    setMenuOpenId(null);
    clearAttachment();
    await fetchMessages(conv.id);
  };

  const startWithPeer = async (peerId) => {
    setError('');
    setLoadingList(true);
    try {
      const data = await postJson('start_conversation.php', {
        user_id: userId,
        peer_id: peerId,
      });
      if (!data.success) {
        setError(data.message || 'Cannot start chat.');
        return;
      }
      const conv = data.conversation;
      setView('chat');
      setActive({ id: conv.id, peer: conv.peer });
      setMessages([]);
      lastMsgIdRef.current = 0;
      setDraft('');
      clearAttachment();
      await fetchMessages(conv.id);
      fetchConversations();
    } catch (err) {
      setError('Network error starting chat.');
    } finally {
      setLoadingList(false);
    }
  };

  const loadContacts = async () => {
    setError('');
    setView('compose');
    setContactQuery('');
    setLoadingList(true);
    try {
      const res = await fetch(`${API_URL}/get_connections.php?user_id=${userId}`);
      const data = await res.json();
      if (data.success) {
        setContacts(data.connections || []);
      } else {
        setContacts([]);
      }
    } catch {
      setContacts([]);
    } finally {
      setLoadingList(false);
    }
  };

  const onPickAttachment = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      setError('File is too large (max 12 MB).');
      e.target.value = '';
      return;
    }
    setError('');
    setAttachment(file);
    if (file.type.startsWith('image/')) {
      setAttachmentPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
    } else {
      setAttachmentPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if ((!text && !attachment) || !active?.id || sending) return;
    setSending(true);
    setError('');
    try {
      let data;
      if (attachment) {
        const form = new FormData();
        form.append('user_id', String(userId));
        form.append('conversation_id', String(active.id));
        form.append('body', text);
        form.append('attachment', attachment);
        const res = await fetch(`${API_URL}/send_message.php`, {
          method: 'POST',
          body: form,
        });
        data = await res.json();
      } else {
        data = await postJson('send_message.php', {
          user_id: userId,
          conversation_id: active.id,
          body: text,
        });
      }
      if (!data.success) {
        setError(data.message || 'Failed to send.');
        return;
      }
      setDraft('');
      clearAttachment();
      const msg = {
        ...data.message,
        reactions: data.message?.reactions || emptyReactions(),
      };
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      lastMsgIdRef.current = Math.max(lastMsgIdRef.current, msg.id);
      fetchConversations();
    } catch {
      setError('Network error sending message.');
    } finally {
      setSending(false);
    }
  };

  const reactToMessage = async (messageId, reactionType, remove = false) => {
    // Optimistic update
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m;
        if (m.is_deleted) return m;
        const prevR = m.reactions || emptyReactions();
        const counts = { ...(prevR.counts || {}) };
        let total = prevR.total || 0;
        const prevMine = prevR.my_reaction;

        if (prevMine && counts[prevMine]) {
          counts[prevMine] = Math.max(0, counts[prevMine] - 1);
          if (counts[prevMine] === 0) delete counts[prevMine];
          total = Math.max(0, total - 1);
        }

        let my_reaction = null;
        if (!remove) {
          counts[reactionType] = (counts[reactionType] || 0) + 1;
          total += 1;
          my_reaction = reactionType;
        }

        return {
          ...m,
          reactions: { counts, my_reaction, total },
        };
      })
    );

    try {
      const data = await postJson('react_message.php', {
        user_id: userId,
        message_id: messageId,
        reaction_type: reactionType,
        remove: Boolean(remove),
      });
      if (data.success && data.reactions) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId ? { ...m, reactions: data.reactions } : m
          )
        );
      }
    } catch (err) {
      console.error('Failed to react', err);
    }
  };

  const startEditMessage = (m) => {
    if (m.is_deleted) return;
    setMenuOpenId(null);
    setEditingId(m.id);
    setEditDraft(m.body || '');
  };

  const cancelEditMessage = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const saveEditMessage = async (messageId) => {
    const text = editDraft.trim();
    if (!text) {
      setError('Edited message cannot be empty.');
      return;
    }
    setError('');
    try {
      const data = await postJson('edit_message.php', {
        user_id: userId,
        message_id: messageId,
        body: text,
      });
      if (!data.success) {
        setError(data.message || 'Failed to edit message.');
        return;
      }
      if (data.message) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? { ...m, ...data.message, reactions: data.message.reactions || m.reactions }
              : m
          )
        );
      }
      cancelEditMessage();
      fetchConversations();
    } catch {
      setError('Network error editing message.');
    }
  };

  const deleteMessage = async (messageId) => {
    setMenuOpenId(null);
    if (!window.confirm('Delete this message?')) return;
    setError('');
    try {
      const data = await postJson('delete_message.php', {
        user_id: userId,
        message_id: messageId,
      });
      if (!data.success) {
        setError(data.message || 'Failed to delete message.');
        return;
      }
      if (data.message) {
        setMessages((prev) =>
          prev.map((m) => (m.id === messageId ? { ...m, ...data.message } : m))
        );
      } else {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId
              ? {
                  ...m,
                  body: '',
                  attachment_url: null,
                  attachment_type: null,
                  attachment_name: null,
                  is_deleted: true,
                  deleted_at: new Date().toISOString(),
                  reactions: emptyReactions(),
                }
              : m
          )
        );
      }
      if (editingId === messageId) cancelEditMessage();
      fetchConversations();
    } catch {
      setError('Network error deleting message.');
    }
  };

  // Close message menu on outside click
  useEffect(() => {
    if (!menuOpenId) return undefined;
    const onDoc = () => setMenuOpenId(null);
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [menuOpenId]);

  // Poll conversations when dock is open; poll thread when in chat
  useEffect(() => {
    if (!userId) return undefined;
    fetchConversations();
    const id = setInterval(fetchConversations, POLL_MS);
    return () => clearInterval(id);
  }, [userId, fetchConversations]);

  useEffect(() => {
    if (!open || view !== 'chat' || !active?.id) return undefined;
    const id = setInterval(() => {
      fetchMessages(active.id, { incremental: true });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [open, view, active?.id, fetchMessages]);

  useEffect(() => {
    if (view === 'chat') {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, view]);

  useEffect(
    () => () => {
      if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
    },
    [attachmentPreview]
  );

  // Open from profile Message button or ?chat=userId
  useEffect(() => {
    if (!userId) return undefined;

    const openPeer = (peerId) => {
      const pid = Number(peerId);
      if (!pid || pid === userId) return;
      setOpen(true);
      startWithPeer(pid);
    };

    const onOpen = (e) => {
      openPeer(e?.detail?.userId);
    };
    window.addEventListener('cool:open-chat', onOpen);

    try {
      const params = new URLSearchParams(window.location.search);
      const chatPeer = params.get('chat');
      if (chatPeer) {
        openPeer(chatPeer);
        params.delete('chat');
        const next = `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash || ''}`;
        window.history.replaceState({}, '', next);
      }
    } catch {
      /* ignore */
    }

    return () => window.removeEventListener('cool:open-chat', onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (!userId) return null;

  const myAvatar = resolveAvatarUrl(user, API_URL);
  const filteredContacts = contacts.filter((c) => {
    const q = contactQuery.trim().toLowerCase();
    if (!q) return true;
    const name = getAuthorDisplayName(c).toLowerCase();
    const un = (c.username || '').toLowerCase();
    return name.includes(q) || un.includes(q);
  });

  const peerName = active?.peer ? getAuthorDisplayName(active.peer) : 'Chat';
  const peerAvatar = active?.peer
    ? resolveAvatarUrl(active.peer, API_URL)
    : myAvatar;

  const canSend = Boolean(draft.trim() || attachment) && !sending;

  return (
    <div className={`msg-dock ${open ? 'msg-dock--open' : ''}`}>
      {!open && (
        <div className="msg-dock-bar">
          <button
            type="button"
            className="msg-dock-bar-profile"
            onClick={() => {
              setOpen(true);
              setView('list');
              fetchConversations();
            }}
            aria-label="Open messaging"
            title="Messaging"
          >
            <img
              src={myAvatar}
              alt=""
              className="msg-dock-avatar"
              onError={handleAvatarError}
            />
            <span className="msg-dock-online" aria-hidden="true" />
            {totalUnread > 0 && (
              <span className="msg-dock-badge">{totalUnread > 99 ? '99+' : totalUnread}</span>
            )}
          </button>
          <button
            type="button"
            className="msg-dock-compose-btn"
            onClick={() => {
              setOpen(true);
              loadContacts();
            }}
            aria-label="New message"
            title="New message"
          >
            <i className="fas fa-edit" aria-hidden="true" />
            <span className="msg-dock-compose-tip">New message</span>
          </button>
        </div>
      )}

      {open && (
        <div className="msg-panel">
          <header className="msg-panel-header">
            {view === 'chat' ? (
              <>
                <button
                  type="button"
                  className="msg-icon-btn"
                  onClick={() => {
                    setView('list');
                    setActive(null);
                    clearAttachment();
                    fetchConversations();
                  }}
                  aria-label="Back to conversations"
                >
                  <i className="fas fa-chevron-left" />
                </button>
                <img
                  src={peerAvatar}
                  alt=""
                  className="msg-dock-avatar msg-dock-avatar--sm"
                  onError={handleAvatarError}
                />
                <div className="msg-panel-heading">
                  <strong>{peerName}</strong>
                  <span>Connection</span>
                </div>
              </>
            ) : view === 'compose' ? (
              <>
                <button
                  type="button"
                  className="msg-icon-btn"
                  onClick={() => setView('list')}
                  aria-label="Back"
                >
                  <i className="fas fa-chevron-left" />
                </button>
                <div className="msg-panel-heading">
                  <strong>New message</strong>
                  <span>Connections only</span>
                </div>
              </>
            ) : (
              <>
                <img
                  src={myAvatar}
                  alt=""
                  className="msg-dock-avatar msg-dock-avatar--sm"
                  onError={handleAvatarError}
                />
                <div className="msg-panel-heading">
                  <strong>Messaging</strong>
                  <span>Accepted connections</span>
                </div>
              </>
            )}
            <div className="msg-panel-actions">
              {view === 'list' && (
                <button
                  type="button"
                  className="msg-icon-btn"
                  onClick={loadContacts}
                  aria-label="New message"
                  title="Message a connection"
                >
                  <i className="fas fa-edit" />
                </button>
              )}
              <button
                type="button"
                className="msg-icon-btn"
                onClick={() => setOpen(false)}
                aria-label="Minimize messaging"
              >
                <i className="fas fa-chevron-down" />
              </button>
            </div>
          </header>

          {error && <div className="msg-error">{error}</div>}

          {view === 'list' && (
            <div className="msg-list">
              {conversations.length === 0 ? (
                <div className="msg-empty">
                  <p>No conversations yet.</p>
                  <p className="msg-hint">
                    Message people you are connected with. Followers must accept a
                    connection request first.
                  </p>
                  <button type="button" className="msg-primary-btn" onClick={loadContacts}>
                    Start a chat
                  </button>
                </div>
              ) : (
                conversations.map((c) => {
                  const name = getAuthorDisplayName(c.peer);
                  const preview = c.last_message?.body || 'Say hello';
                  return (
                    <button
                      type="button"
                      key={c.id}
                      className={`msg-list-item ${c.unread_count ? 'msg-list-item--unread' : ''}`}
                      onClick={() => openConversation(c)}
                    >
                      <img
                        src={resolveAvatarUrl(c.peer, API_URL)}
                        alt=""
                        className="msg-dock-avatar"
                        onError={handleAvatarError}
                      />
                      <span className="msg-list-body">
                        <span className="msg-list-top">
                          <strong>{name}</strong>
                          <time>{formatTime(c.last_message?.created_at || c.last_message_at)}</time>
                        </span>
                        <span className="msg-list-preview">{preview}</span>
                      </span>
                      {c.unread_count > 0 && (
                        <span className="msg-dock-badge">{c.unread_count}</span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          )}

          {view === 'compose' && (
            <div className="msg-compose">
              <input
                type="search"
                className="msg-search"
                placeholder="Search connections…"
                value={contactQuery}
                onChange={(e) => setContactQuery(e.target.value)}
              />
              {loadingList ? (
                <div className="msg-empty">Loading…</div>
              ) : filteredContacts.length === 0 ? (
                <div className="msg-empty">
                  <p>No connections found.</p>
                  <p className="msg-hint">
                    Connect with someone and wait for them to accept, then you can chat.
                  </p>
                </div>
              ) : (
                <div className="msg-list">
                  {filteredContacts.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      className="msg-list-item"
                      onClick={() => startWithPeer(c.id)}
                    >
                      <img
                        src={resolveAvatarUrl(c, API_URL)}
                        alt=""
                        className="msg-dock-avatar"
                        onError={handleAvatarError}
                      />
                      <span className="msg-list-body">
                        <span className="msg-list-top">
                          <strong>{getAuthorDisplayName(c)}</strong>
                        </span>
                        {c.username && (
                          <span className="msg-list-preview">@{c.username}</span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {view === 'chat' && active && (
            <div className="msg-thread">
              <div className="msg-thread-scroll">
                {messages.length === 0 && (
                  <div className="msg-empty msg-empty--thread">
                    No messages yet. Say hello!
                  </div>
                )}
                {messages.map((m) => {
                  const mine = m.is_mine || m.sender_id === userId;
                  const avatarSrc = mine ? myAvatar : peerAvatar;
                  const isEditing = editingId === m.id;
                  const deleted = Boolean(m.is_deleted || m.deleted_at);
                  return (
                    <div
                      key={m.id}
                      className={`msg-row ${mine ? 'msg-row--mine' : 'msg-row--theirs'}`}
                    >
                      {!mine && (
                        <img
                          src={avatarSrc}
                          alt=""
                          className="msg-bubble-avatar"
                          onError={handleAvatarError}
                        />
                      )}
                      <div className="msg-bubble-col">
                        <div
                          className={`msg-bubble ${mine ? 'msg-bubble--mine' : 'msg-bubble--theirs'} ${
                            deleted ? 'msg-bubble--deleted' : ''
                          }`}
                        >
                          {mine && !deleted && (
                            <div className="msg-bubble-actions">
                              <button
                                type="button"
                                className="msg-more-btn"
                                aria-label="Message options"
                                title="More"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuOpenId((id) => (id === m.id ? null : m.id));
                                }}
                              >
                                <i className="fas fa-ellipsis-h" />
                              </button>
                              {menuOpenId === m.id && (
                                <div
                                  className="msg-more-menu"
                                  onClick={(e) => e.stopPropagation()}
                                  role="menu"
                                >
                                  <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => startEditMessage(m)}
                                  >
                                    <i className="fas fa-pen" aria-hidden="true" /> Edit
                                  </button>
                                  <button
                                    type="button"
                                    role="menuitem"
                                    className="msg-more-menu--danger"
                                    onClick={() => deleteMessage(m.id)}
                                  >
                                    <i className="fas fa-trash" aria-hidden="true" /> Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          )}

                          {deleted ? (
                            <p className="msg-deleted-text">This message was deleted</p>
                          ) : isEditing ? (
                            <div className="msg-edit-box">
                              <textarea
                                value={editDraft}
                                onChange={(e) => setEditDraft(e.target.value)}
                                maxLength={4000}
                                rows={3}
                                autoFocus
                              />
                              <div className="msg-edit-actions">
                                <button
                                  type="button"
                                  className="msg-edit-cancel"
                                  onClick={cancelEditMessage}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="msg-edit-save"
                                  disabled={!editDraft.trim()}
                                  onClick={() => saveEditMessage(m.id)}
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {m.attachment_url && isImageAttachment(m.attachment_type) && (
                                <a
                                  href={m.attachment_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="msg-attach-image-link"
                                >
                                  <img
                                    src={m.attachment_url}
                                    alt={m.attachment_name || 'Photo'}
                                    className="msg-attach-image"
                                  />
                                </a>
                              )}
                              {m.attachment_url && !isImageAttachment(m.attachment_type) && (
                                <a
                                  href={m.attachment_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="msg-attach-file"
                                  download={m.attachment_name || undefined}
                                >
                                  <i className="fas fa-file-alt" aria-hidden="true" />
                                  <span>{m.attachment_name || 'Download file'}</span>
                                </a>
                              )}
                              {m.body ? <p>{m.body}</p> : null}
                            </>
                          )}
                          <time>
                            {formatTime(m.created_at)}
                            {!deleted && m.edited_at ? ' · Edited' : ''}
                          </time>
                        </div>
                        {!deleted && (
                          <MessageReactionBar message={m} onReact={reactToMessage} />
                        )}
                      </div>
                      {mine && (
                        <img
                          src={avatarSrc}
                          alt=""
                          className="msg-bubble-avatar"
                          onError={handleAvatarError}
                        />
                      )}
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {(attachment || attachmentPreview) && (
                <div className="msg-attach-preview">
                  {attachmentPreview ? (
                    <img src={attachmentPreview} alt="" />
                  ) : (
                    <span className="msg-attach-preview-file">
                      <i className="fas fa-paperclip" aria-hidden="true" />
                      {attachment?.name}
                    </span>
                  )}
                  <button
                    type="button"
                    className="msg-attach-clear"
                    onClick={clearAttachment}
                    aria-label="Remove attachment"
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              )}

              <form className="msg-composer" onSubmit={sendMessage}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_FILES}
                  className="msg-file-input"
                  onChange={onPickAttachment}
                  aria-hidden="true"
                  tabIndex={-1}
                />
                <button
                  type="button"
                  className="msg-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Attach photo or file"
                  title="Attach photo or file"
                >
                  <i className="fas fa-paperclip" />
                </button>
                <input
                  type="text"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Write a message…"
                  maxLength={4000}
                  autoComplete="off"
                />
                <button type="submit" disabled={!canSend} aria-label="Send">
                  <i className="fas fa-paper-plane" />
                </button>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
